import React, { useEffect, useState } from "react";
import MasterPage from "@/components/MasterPage";
import { money } from "@/lib/api";
import { INDIAN_STATES } from "@/lib/states";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Broom } from "@phosphor-icons/react";
import ImportItemsDialog from "@/components/ImportItemsDialog";
import DuplicateItemsDialog from "@/components/DuplicateItemsDialog";

export const Vendors = () => (
  <MasterPage
    title="Vendors"
    endpoint="/vendors"
    testidPrefix="vendors"
    fields={[
      { name: "vendor_name", label: "Vendor / Company Name", required: true },
      { name: "contact_person", label: "Contact Person" },
      { name: "mobile", label: "Mobile" },
      { name: "email", label: "Email" },
      { name: "gst_no", label: "GST No" },
      { name: "pan_no", label: "PAN No" },
      { name: "city", label: "City" },
      { name: "state", label: "State", type: "select",
        options: [{ value: "", label: "Select State" }, ...INDIAN_STATES.map(s => ({ value: s, label: s }))] },
      { name: "address", label: "Address", type: "textarea", full: true },
    ]}
    columns={[
      { key: "vendor_name", label: "Vendor Name" },
      { key: "contact_person", label: "Contact" },
      { key: "mobile", label: "Mobile" },
      { key: "state", label: "State" },
      { key: "gst_no", label: "GST No" },
    ]}
  />
);

export const Customers = () => {
  const [ctype, setCtype] = useState("individual");
  const [gstTreatment, setGstTreatment] = useState("");
  const stateOpts = [{ value: "", label: "Select State" }, ...INDIAN_STATES.map(s => ({ value: s, label: s }))];
  const copyBilling = (form, setForm) => {
    setForm({
      ...form,
      shipping_attention: form.billing_attention || "",
      shipping_country: form.billing_country || "India",
      shipping_street1: form.billing_street1 || "",
      shipping_street2: form.billing_street2 || "",
      shipping_city: form.billing_city || "",
      shipping_state: form.billing_state || "",
      shipping_pincode: form.billing_pincode || "",
    });
  };
  return (
    <MasterPage
      title="Customers"
      endpoint="/customers"
      testidPrefix="customers"
      onFormChange={(v) => { setCtype(v.customer_type || "individual"); setGstTreatment(v.gst_treatment || ""); }}
      fields={[
        { name: "_sec_type", label: "Customer Type", type: "section" },
        { name: "customer_type", label: "Customer Type", type: "select", default: "individual",
          options: [
            { value: "individual", label: "Individual" },
            { value: "business", label: "Business" },
          ] },
        { name: "salutation", label: "Salutation", type: "select", default: "",
          options: [
            { value: "", label: "—" },
            { value: "Mr", label: "Mr." },
            { value: "Ms", label: "Ms." },
            { value: "Mrs", label: "Mrs." },
            { value: "Dr", label: "Dr." },
          ] },
        { name: "first_name", label: "First Name" },
        { name: "last_name", label: "Last Name" },
        ...(ctype === "business" ? [{ name: "company_name", label: "Company Name" }] : []),
        { name: "display_name", label: "Display Name*", required: true },
        { name: "email", label: "Email", type: "email" },
        { name: "work_phone", label: "Work Phone" },
        { name: "mobile", label: "Mobile", required: true },

        { name: "_sec_tax", label: "Tax Info (GST)", type: "section" },
        { name: "gst_treatment", label: "GST Treatment", type: "select", default: "",
          options: [
            { value: "", label: "— Select —" },
            { value: "registered", label: "Registered Business - Regular" },
            { value: "composition", label: "Registered Business - Composition" },
            { value: "unregistered", label: "Unregistered Business" },
            { value: "consumer", label: "Consumer" },
            { value: "overseas", label: "Overseas" },
          ] },
        { name: "place_of_supply", label: "Place of Supply", type: "select", default: "", options: stateOpts },
        ...(["registered", "composition"].includes(gstTreatment) ? [{ name: "gst_no", label: "GSTIN", required: true }] : []),
        { name: "pan", label: "PAN" },
        { name: "tax_preference", label: "Tax Preference", type: "select", default: "taxable",
          options: [
            { value: "taxable", label: "Taxable" },
            { value: "tax_exempt", label: "Tax Exempt" },
          ] },

        { name: "_sec_billing", label: "Billing Address", type: "section" },
        { name: "billing_attention", label: "Attention" },
        { name: "billing_country", label: "Country", default: "India" },
        { name: "billing_street1", label: "Street 1", full: true },
        { name: "billing_street2", label: "Street 2", full: true },
        { name: "billing_city", label: "City" },
        { name: "billing_state", label: "State", type: "select", default: "", options: stateOpts },
        { name: "billing_pincode", label: "Pincode" },

        { name: "_sec_shipping", label: "Shipping Address", type: "section", action: copyBilling, actionLabel: "↺ Copy billing address" },
        { name: "shipping_attention", label: "Attention" },
        { name: "shipping_country", label: "Country", default: "India" },
        { name: "shipping_street1", label: "Street 1", full: true },
        { name: "shipping_street2", label: "Street 2", full: true },
        { name: "shipping_city", label: "City" },
        { name: "shipping_state", label: "State", type: "select", default: "", options: stateOpts },
        { name: "shipping_pincode", label: "Pincode" },

        { name: "_sec_remarks", label: "Notes", type: "section" },
        { name: "remarks", label: "Remarks", type: "textarea", full: true },
      ]}
      columns={[
        { key: "customer_name", label: "Customer Name" },
        { key: "customer_type", label: "Type", render: r => (
          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded font-semibold ${
            r.customer_type === "business" ? "bg-blue-100 text-blue-700" : "bg-secondary text-muted-foreground"
          }`}>{r.customer_type || "individual"}</span>
        )},
        { key: "mobile", label: "Mobile" },
        { key: "billing_city", label: "City", render: r => r.billing_city || r.city || "" },
        { key: "billing_state", label: "State", render: r => r.billing_state || r.state || "" },
        { key: "gst_no", label: "GSTIN" },
      ]}
    />
  );
};

export const Technicians = () => (
  <MasterPage
    title="Technicians"
    endpoint="/technicians"
    testidPrefix="technicians"
    fields={[
      { name: "technician_name", label: "Technician Name", required: true },
      { name: "mobile", label: "Mobile" },
      { name: "email", label: "Email" },
      { name: "joining_date", label: "Joining Date", type: "date" },
      { name: "salary", label: "Salary", type: "number", default: 0 },
      { name: "status", label: "Status", type: "select", default: "active",
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ] },
    ]}
    columns={[
      { key: "technician_name", label: "Name" },
      { key: "mobile", label: "Mobile" },
      { key: "joining_date", label: "Joined" },
      { key: "salary", label: "Salary", render: r => money(r.salary) },
      { key: "status", label: "Status", render: r => (
        <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded font-semibold ${
          r.status === "active" ? "bg-green-100 text-green-700" : "bg-secondary text-muted-foreground"
        }`}>{r.status}</span>
      )},
    ]}
  />
);

export const Items = () => {
  const [brands, setBrands] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [dupeOpen, setDupeOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    api.get("/items/brands").then(r => setBrands(r.data));
  }, [reloadKey]);
  return (
    <>
      <MasterPage
        title="Items"
        endpoint="/items"
        testidPrefix="items"
        reloadKey={reloadKey}
        serverSearch={true}
        extraHeaderActions={
          <>
            <Button variant="outline" onClick={() => setDupeOpen(true)} data-testid="items-dedupe-button">
              <Broom size={14} className="mr-1" /> Remove Duplicates
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="items-import-button">
              <Upload size={14} className="mr-1" /> Import from Excel
            </Button>
          </>
        }
        fields={[
          { name: "item_type", label: "Type", type: "select", default: "GOODS",
            options: [
              { value: "GOODS", label: "Goods (physical item, tracks stock)" },
              { value: "SERVICE", label: "Service (no stock — labour / install / visit charges)" },
            ] },
          { name: "item_code", label: "Item Code", required: true },
          { name: "item_name", label: "Item Name", required: true },
          { name: "brand", label: "Brand (select or type new)", type: "datalist", listId: "brands-list", options: brands },
          { name: "category", label: "Category" },
          { name: "hsn_code", label: "HSN / SAC Code" },
          { name: "gst_rate", label: "GST Rate (%)", type: "select", default: "18",
            options: [
              { value: "0", label: "0%" },
              { value: "5", label: "5%" },
              { value: "12", label: "12%" },
              { value: "18", label: "18%" },
              { value: "28", label: "28%" },
            ] },
          { name: "unit", label: "Unit", default: "PCS" },
          { name: "purchase_price", label: "Purchase Price (₹)", type: "number", default: 0 },
          { name: "purchase_inclusive", label: "Purchase price INCLUDES GST?", type: "select", default: "false",
            options: [
              { value: "false", label: "No — enter net rate (excluding GST)" },
              { value: "true", label: "Yes — includes GST (system will back-calculate)" },
            ] },
          { name: "sale_price", label: "Sale Price (₹)", type: "number", default: 0 },
          { name: "sale_inclusive", label: "Sale price INCLUDES GST?", type: "select", default: "false",
            options: [
              { value: "false", label: "No — net rate (GST added on top at billing)" },
              { value: "true", label: "Yes — MRP with GST (system back-calculates net)" },
            ] },
          { name: "current_stock", label: "Current Stock (ignored for Service)", type: "number", default: 0 },
          { name: "reorder_level", label: "Reorder Level", type: "number", default: 0 },
        ]}
        columns={[
          { key: "item_type", label: "Type", render: r => (
            (r.item_type || "GOODS") === "SERVICE"
              ? <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">SERVICE</span>
              : <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700">GOODS</span>
          ) },
          { key: "item_code", label: "Code" },
          { key: "item_name", label: "Item Name" },
          { key: "brand", label: "Brand" },
          { key: "gst_rate", label: "GST", render: r => `${r.gst_rate}%` },
          { key: "purchase_price", label: "Purchase", render: r => money(r.purchase_price) },
          { key: "sale_price", label: "Sale", render: r => money(r.sale_price) },
          { key: "current_stock", label: "Stock", render: r => (
            (r.item_type || "GOODS") === "SERVICE"
              ? <span className="text-muted-foreground text-xs">—</span>
              : <span className={r.current_stock <= r.reorder_level ? "text-destructive font-semibold" : ""}>
                  {r.current_stock}
                </span>
          )},
        ]}
      />
      <ImportItemsDialog open={importOpen} onOpenChange={setImportOpen} onImported={() => setReloadKey(k => k + 1)} />
      <DuplicateItemsDialog open={dupeOpen} onOpenChange={setDupeOpen} onCleaned={() => setReloadKey(k => k + 1)} />
    </>
  );
};
